import { AdditiveBlending, BufferAttribute, BufferGeometry, DoubleSide, Mesh, MeshBasicMaterial, Vector3 } from 'three';
import type { Color } from 'three';

const length = 14;
/** How fast a fist must travel, metres a second, before it leaves a streak: a punch, not a guard shuffling. */
const streakSpeed = 3.2;

const side = new Vector3();
const along = new Vector3();
const toCamera = new Vector3();

/**
 * The smear a punching glove leaves in the air: a ribbon through where the fist has just been, facing the camera,
 * widest at the glove and fading to nothing behind it. It shows only while the fist is fast.
 */
export class Trail {
  readonly mesh: Mesh;
  private readonly points = Array.from({ length }, () => new Vector3());
  private readonly positions = new Float32Array(length * 2 * 3);
  private readonly colours = new Float32Array(length * 2 * 3);
  private readonly last = new Vector3();
  private strength = 0;
  private started = false;

  constructor(private readonly colour: Color, private readonly width: number) {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new BufferAttribute(this.colours, 3));
    const index: number[] = [];
    for (let i = 0; i < length - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geometry.setIndex(index);
    this.mesh = new Mesh(geometry, new MeshBasicMaterial({ vertexColors: true, transparent: true, blending: AdditiveBlending, depthWrite: false, side: DoubleSide }));
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
  }

  /** Out at once, and started again wherever the fist is next: for when it jumps with the camera, not a punch. */
  putOut(): void {
    this.started = false;
    this.strength = 0;
    this.mesh.visible = false;
  }

  /** Every frame, with the fist's place in the world and the camera's. */
  update(seconds: number, fist: Vector3, camera: Vector3): void {
    if (!this.started) {
      this.points.forEach((p) => p.copy(fist));
      this.last.copy(fist);
      this.started = true;
    }
    const speed = seconds > 0 ? fist.distanceTo(this.last) / seconds : 0;
    this.last.copy(fist);
    this.strength = Math.max(speed > streakSpeed ? 1 : 0, this.strength - seconds * 6);
    for (let i = length - 1; i > 0; i--) this.points[i].copy(this.points[i - 1]);
    this.points[0].copy(fist);
    for (let i = 0; i < length; i++) {
      const p = this.points[i];
      along.subVectors(this.points[Math.max(0, i - 1)], this.points[Math.min(length - 1, i + 1)]);
      toCamera.subVectors(camera, p);
      side.crossVectors(along, toCamera).normalize();
      const fade = 1 - i / (length - 1);
      const half = this.width * fade;
      this.positions.set([p.x + side.x * half, p.y + side.y * half, p.z + side.z * half, p.x - side.x * half, p.y - side.y * half, p.z - side.z * half], i * 6);
      const glow = this.strength * fade * fade * 0.4;
      this.colours.set([this.colour.r * glow, this.colour.g * glow, this.colour.b * glow, this.colour.r * glow, this.colour.g * glow, this.colour.b * glow], i * 6);
    }
    this.mesh.geometry.attributes.position.needsUpdate = true;
    this.mesh.geometry.attributes.color.needsUpdate = true;
    this.mesh.visible = this.strength > 0;
  }
}
